import React, { useState } from 'react';

import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import LinkButton from '@components/button/LinkButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import Title from 'lib/src/components/typography/Title';
import { AdminUser } from 'src/types/shared/AdminUser';
import { userRoleValues } from '../../../types/shared/User';
import ActionButton from 'lib/src/components/button/ActionButton';
import UpgradeAdminModal from '../adminUsers/UpgradeAdminModal';
import DowngradeAdminModal from '../adminUsers/DowngradeAdminModal';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import DataCheck from 'lib/src/components/common/DataCheck';
import { useSelector } from 'react-redux';
import { getAdminUsersError, getAdminUsersIsFetching } from '@selectors/adminUsers';
import dayjs from 'dayjs';

const AdminUserComponent: React.FC<AdminUserProps> = ({ user, hasSuperAdminAccess }) => {
    const isFetching = useSelector(getAdminUsersIsFetching);
    const fetchError = useSelector(getAdminUsersError);

    const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
    const [showDowngradeModal, setShowDowngradeModal] = useState<boolean>(false);

    return (
        <DataCheck isFetching={isFetching} error={fetchError} dataExists={!!user}>
            <Title>
                Admin - {user?.firstName} {user?.lastName}
            </Title>

            <ContentBlock>
                <ContentRow>
                    <ContentItem label="Name">
                        <p>{`${user?.firstName} ${user?.lastName}`}</p>
                    </ContentItem>
                    <ContentItem label="Email">
                        <p>
                            <a href={`mailto:${user?.email}`}>{user?.email}</a>
                        </p>
                    </ContentItem>
                    <ContentItem label="Roles">
                        <p>{user?.roles.map(role => userRoleValues[role]).join(', ')}</p>
                    </ContentItem>
                </ContentRow>
                <ContentRow>
                    <ContentItem label="MFA Enabled">
                        <p>{user?.hasMfa ? 'Yes' : 'No'}</p>
                    </ContentItem>
                    <ContentItem label="MFA Enabled On">
                        <p>
                            {user?.mfaEnabledOn
                                ? dayjs(user.mfaEnabledOn).format('DD/MM/YYYY HH:mm')
                                : 'N/A'}
                        </p>
                    </ContentItem>
                    <ContentItem></ContentItem>
                </ContentRow>
            </ContentBlock>

            <ButtonRow alignment="left">
                <LinkButton color="grey" href={`/admins/${user?.id}/edit`}>
                    Edit
                </LinkButton>
                <LinkButton color="grey" href={`/admins/${user?.id}/edit-password`}>
                    Edit password
                </LinkButton>
                {hasSuperAdminAccess && (
                    <>
                        {!user?.roles.includes(userRoles.superAdmin) ? (
                            <ActionButton
                                color="blue"
                                icon="crown"
                                onClick={() => setShowUpgradeModal(true)}
                            >
                                Upgrade to Super Admin
                            </ActionButton>
                        ) : (
                            <ActionButton
                                color="blue"
                                icon="users-cog"
                                onClick={() => setShowDowngradeModal(true)}
                            >
                                Downgrade to Admin
                            </ActionButton>
                        )}
                        {user?.hasMfa && (
                            <LinkButton color="blue" href={`/admins/${user?.id}/revoke-mfa`}>
                                Revoke MFA
                            </LinkButton>
                        )}
                    </>
                )}
                <LinkButton color="red" href={`/admins/${user?.id}/delete`}>
                    Delete
                </LinkButton>
            </ButtonRow>
            {!!showUpgradeModal && !!user && (
                <UpgradeAdminModal user={user} closeModal={() => setShowUpgradeModal(false)} />
            )}
            {!!showDowngradeModal && !!user && (
                <DowngradeAdminModal user={user} closeModal={() => setShowDowngradeModal(false)} />
            )}
        </DataCheck>
    );
};

interface AdminUserProps {
    user: AdminUser | undefined;
    hasSuperAdminAccess: boolean;
}

export default AdminUserComponent;
